import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, IconButton, CircularProgress, Alert, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Avatar, Chip, TablePagination, Menu, MenuItem, ListItemIcon as MuiListItemIcon,
    TextField, InputAdornment
} from '@mui/material';
import { Add, Refresh, Edit, Delete, MoreVert, Search, Category } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllFoodCategories, deleteFoodCategory } from '../../services/Api';
import DeleteConfirm from '../../modals/DeleteConfirm';
import { useCallback } from 'react';

const GetAllFoodCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const [deleteError, setDeleteError] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        setError(null);
        setDeleteError(null);
        try {
            const response = await getAllFoodCategories(page + 1, rowsPerPage, debouncedSearch);
            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.categories)) targetArray = src.categories;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                }

                const total = response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || 0;
                setCategories(targetArray);
                setTotalRows(total);
            } else {
                setError(response.message || 'Failed to fetch food categories');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching food categories');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedSearch]);

    useEffect(() => {
        fetchCategories();
    }, [page, rowsPerPage, debouncedSearch, fetchCategories]);

    const handleMenuOpen = (event, category) => {
        setAnchorEl(event.currentTarget);
        setSelectedCategory(category);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCategory(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = () => {
        setCategoryToDelete(selectedCategory);
        setDeleteDialogOpen(true);
        handleMenuClose();
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const response = await deleteFoodCategory(categoryToDelete.id);
            if (response.success || response.message) {
                setDeleteDialogOpen(false);
                fetchCategories();
            } else {
                setDeleteError(response.message || 'Failed to delete category');
            }
        } catch (err) {
            console.error(err);
            setDeleteError(err.response?.data?.message || 'An error occurred while deleting the category');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, textTransform: 'capitalize' }}>
                        Food Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage available categories for food vendors and menus
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Search food categories..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ 
                            width: 250,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: '20px', 
                            } 
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <IconButton onClick={fetchCategories} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <Refresh />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/food-categories/create')}
                        sx={{ px: 3, backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
                    >
                        Add Category
                    </Button>
                </Box>
            </Box>

            {deleteError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeleteError(null)}>
                    {deleteError}
                </Alert>
            )}

            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Display Order</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                            Loading categories...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                        <Alert severity="error" sx={{ width: 'fit-content', mx: 'auto' }}>
                                            {error}
                                        </Alert>
                                        <Button onClick={fetchCategories} sx={{ mt: 2 }}>Try Again</Button>
                                    </TableCell>
                                </TableRow>
                            ) : categories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No food categories found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                categories.map((category) => (
                                    <TableRow key={category.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    variant="rounded"
                                                    src={category.image}
                                                    alt={category.name}
                                                    sx={{ width: 48, height: 48, backgroundColor: 'rgba(0,0,0,0.05)' }}
                                                >
                                                    <Category sx={{ color: 'text.secondary' }} />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {category.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{category.displayOrder || 0}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={category.isActive ? "Active" : "Inactive"}
                                                size="small"
                                                color={category.isActive ? "success" : "default"}
                                                variant={category.isActive ? "filled" : "outlined"}
                                                sx={{ fontWeight: 500, minWidth: 70 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, category)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={2}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { navigate(`/food-categories/modify/${selectedCategory?.id}`, { state: { category: selectedCategory } }); handleMenuClose(); }}>
                    <MuiListItemIcon><Edit fontSize="small" /></MuiListItemIcon>
                    Edit Category
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete Category
                </MenuItem>
            </Menu>

            {/* Delete Confirmation */}
            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Food Category?"
                message={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
            />
        </Box>
    );
};

export default GetAllFoodCategories;
